import { useTranslation } from '../../i18n';

interface CaptionConfig {
  color?: string;
  accent_color?: string;
  font_size?: number;
  accent_font_size?: number;
  words_per_line?: number;
  vertical_margin?: number; // Kept for type completeness, though not displayed here
  style_id?: string; // Kept for type completeness, though not displayed here
}

interface BestMomentsConfigProps {
  config: {
    captions_enabled?: boolean;
    caption_config: CaptionConfig;
  };
  // onConfigChange prop is removed as this component is display-only
}

/**
 * BestMomentsConfig component
 * 
 * This component is used to display caption configuration in the best moments feature.
 */
export function BestMomentsConfig({ config }: BestMomentsConfigProps) {
  const { t } = useTranslation();
  const captionConfig: CaptionConfig = config.caption_config || {};
  
  // Examples based on current values or defaults
  const primaryColorExample = captionConfig.color || '#FFFFFF';
  const accentColorExample = captionConfig.accent_color || '#7FFF00';
  const wordsPerLineExample = captionConfig.words_per_line || 4;
  const primaryFontSizeExample = captionConfig.font_size || 24; // Default example size
  const accentFontSizeExample = captionConfig.accent_font_size || 20; // Default example size

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="col-span-1">
          <h3 className="text-lg font-semibold mb-3 text-content-strong">{t('bestMomentsConfig.fontColorsHeader')}</h3>
          
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm mb-1 text-label">{t('bestMomentsConfig.primaryColorLabel')}</label>
              <div className="flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded-full border border-default shadow-sm"
                  style={{ backgroundColor: primaryColorExample }}
                >
                </div>
                <span className="text-xs font-mono text-muted">{primaryColorExample}</span>
              </div>
              <div className="mt-2">
                <p className="text-sm text-caption">Example:</p>
                <span className="font-semibold" style={{ color: primaryColorExample }}>
                  Primary text
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm mb-1 text-label">{t('bestMomentsConfig.accentColorLabel')}</label>
              <div className="flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded-full border border-default shadow-sm"
                  style={{ backgroundColor: accentColorExample }}
                >
                </div>
                <span className="text-xs font-mono text-muted">{accentColorExample}</span>
              </div>
              <div className="mt-2">
                <p className="text-sm text-caption">Example:</p>
                <span className="font-semibold" style={{ color: accentColorExample }}>
                  Accent text
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-span-1">
          <h3 className="text-lg font-semibold mb-3 text-content-strong">{t('bestMomentsConfig.textLayoutHeader')}</h3>
          
          <div className="mb-4">
            <label className="block text-sm mb-1 text-label">{t('bestMomentsConfig.wordsPerLineLabel')}</label>
            <div className="text-lg font-medium text-content-strong">
              {wordsPerLineExample} {/* Display value directly */}
            </div>
            <div className="mt-2 text-sm text-muted">
              <p>{t('bestMomentsConfig.sampleCaptionText')}</p>
              <span className="inline-block max-w-xs text-center font-medium" style={{ color: primaryColorExample }}>
                {Array(wordsPerLineExample).fill('Example').join(' ')}
              </span>
            </div>
          </div>
        </div>
        
        <div className="col-span-1">
          <h3 className="text-lg font-semibold mb-3 text-content-strong">{t('bestMomentsConfig.fontSizesHeader')}</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1 text-label">{t('bestMomentsConfig.primarySizeLabel')}</label>
              <div className="text-lg font-medium text-content-strong">
                {primaryFontSizeExample}px {/* Display value directly */}
              </div>
              <div className="text-sm text-body" style={{ fontSize: `${primaryFontSizeExample / 16}rem` }}>
                Sample
              </div>
            </div>
            
            <div>
              <label className="block text-sm mb-1 text-label">{t('bestMomentsConfig.accentSizeLabel')}</label>
              <div className="text-lg font-medium text-content-strong">
                {accentFontSizeExample}px {/* Display value directly */}
              </div>
              <div className="text-sm text-body" style={{ fontSize: `${accentFontSizeExample / 16}rem` }}>
                Sample
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
